export type IsoInstant = string;
export type IsoDate = string;
export type Money = { amount: string; currency: string };

const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/;

export const browserTimeZone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone;

const parseInstant = (value: IsoInstant): Date => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime()) || !value.endsWith('Z'))
    throw new Error(`Expected a UTC ISO 8601 instant, received ${value}`);
  return date;
};

export const formatDateTime = (
  value: IsoInstant,
  locale: string,
  options: { style?: 'short' | 'medium' | 'long'; timeZone?: string } = {},
): string => {
  const date = parseInstant(value);
  const dateStyle = options.style ?? 'medium';
  return new Intl.DateTimeFormat(locale, {
    dateStyle,
    timeStyle: 'short',
    timeZone: options.timeZone ?? browserTimeZone(),
  }).format(date);
};

export const formatTime = (value: IsoInstant, locale: string, timeZone: string = browserTimeZone()): string =>
  new Intl.DateTimeFormat(locale, { timeStyle: 'short', timeZone }).format(parseInstant(value));

const pad = (value: number): string => String(value).padStart(2, '0');

export const calendarDayOf = (value: IsoInstant, timeZone: string = browserTimeZone()): IsoDate => {
  const parts = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    timeZone,
  }).formatToParts(parseInstant(value));
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((entry) => entry.type === type)?.value);
  return `${String(part('year'))}-${pad(part('month'))}-${pad(part('day'))}`;
};

const SECONDS_PER_UNIT: readonly (readonly [Intl.RelativeTimeFormatUnit, number])[] = [
  ['day', 86_400],
  ['hour', 3_600],
  ['minute', 60],
];

const RELATIVE_LIMIT_DAYS = 7;
const MILLISECONDS_PER_SECOND = 1000;

export const formatTimeAgo = (value: IsoInstant, locale: string, now: Date): string => {
  const date = parseInstant(value);
  const elapsed = Math.max(0, Math.round((now.getTime() - date.getTime()) / MILLISECONDS_PER_SECOND));
  const [unit, seconds] = SECONDS_PER_UNIT.find(([, size]) => elapsed >= size) ?? ['second', 1];
  const amount = Math.floor(elapsed / seconds);
  if (unit === 'day' && amount >= RELATIVE_LIMIT_DAYS)
    return formatDateTime(value, locale, { style: 'short' });
  return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(
    unit === 'second' ? 0 : -amount,
    unit,
  );
};

export type PeriodUnit = 'hour' | 'day' | 'week' | 'month';

const PERIOD_START_PARTS: Readonly<Record<PeriodUnit, Intl.DateTimeFormatOptions>> = {
  hour: { hour: '2-digit', minute: '2-digit' },
  day: { day: 'numeric', month: 'short' },
  week: { day: 'numeric', month: 'short' },
  month: { month: 'short', year: 'numeric' },
};

export const formatPeriodStart = (
  value: IsoInstant,
  locale: string,
  unit: PeriodUnit,
  timeZone: string = browserTimeZone(),
): string =>
  new Intl.DateTimeFormat(locale, { ...PERIOD_START_PARTS[unit], timeZone }).format(parseInstant(value));

export const formatDate = (
  value: IsoDate,
  locale: string,
  style: 'short' | 'medium' | 'long' = 'medium',
): string => {
  if (!CALENDAR_DATE.test(value)) throw new Error(`Expected a calendar date YYYY-MM-DD, received ${value}`);
  const [year, month, day] = value.split('-').map(Number) as [number, number, number];
  return new Intl.DateTimeFormat(locale, { dateStyle: style, timeZone: 'UTC' }).format(
    new Date(Date.UTC(year, month - 1, day)),
  );
};

const moneyFormatOf = (money: Money, locale: string) => {
  const amount = Number(money.amount);
  if (!Number.isFinite(amount)) throw new Error(`Expected a decimal amount, received ${money.amount}`);
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: money.currency,
    currencyDisplay: 'code',
  });
  return { formatter, amount };
};

export const formatMoney = (money: Money, locale: string): string => {
  const { formatter, amount } = moneyFormatOf(money, locale);
  return formatter.format(amount);
};

export type MoneyPart = { readonly kind: 'currency' | 'figure'; readonly text: string };

export const formatMoneyParts = (money: Money, locale: string): readonly MoneyPart[] => {
  const { formatter, amount } = moneyFormatOf(money, locale);
  return formatter.formatToParts(amount).map((part) => ({
    kind: part.type === 'currency' ? 'currency' : 'figure',
    text: part.value,
  }));
};

export const formatNumber = (value: number, locale: string, maximumFractionDigits = 0): string =>
  new Intl.NumberFormat(locale, { maximumFractionDigits }).format(value);

export const formatPercent = (ratio: number, locale: string): string =>
  new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 0 }).format(ratio);

const CHANGE_FRACTION_DIGITS = 1;

export const formatChange = (ratio: number, locale: string): string =>
  new Intl.NumberFormat(locale, {
    style: 'percent',
    maximumFractionDigits: CHANGE_FRACTION_DIGITS,
    signDisplay: 'exceptZero',
  }).format(ratio);

export const formatRate = (ratio: number, locale: string): string =>
  new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: CHANGE_FRACTION_DIGITS }).format(
    ratio,
  );

export const formatSignedNumber = (value: number, locale: string): string =>
  new Intl.NumberFormat(locale, {
    maximumFractionDigits: CHANGE_FRACTION_DIGITS,
    signDisplay: 'exceptZero',
  }).format(value);

export const formatList = (items: readonly string[], locale: string): string =>
  new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(items);

export const formatLanguage = (code: string, locale: string): string =>
  new Intl.DisplayNames([locale], { type: 'language' }).of(code) ?? code;

export const formatRegion = (code: string, locale: string): string =>
  new Intl.DisplayNames([locale], { type: 'region' }).of(code) ?? code;

const DIGITS = /^\d+$/u;
const MAX_INTEGER_DIGITS = 11;
const MAX_FRACTION_DIGITS = 3;

export const MAX_DECIMAL_LENGTH = MAX_INTEGER_DIGITS + MAX_FRACTION_DIGITS + 1;

export const isDecimalAmount = (value: string): boolean => {
  const parts = value.split('.');
  if (parts.length > 2) return false;
  const [integerPart, fractionPart] = parts;
  if (integerPart === undefined || !DIGITS.test(integerPart) || integerPart.length > MAX_INTEGER_DIGITS)
    return false;
  if (fractionPart === undefined) return true;
  return DIGITS.test(fractionPart) && fractionPart.length <= MAX_FRACTION_DIGITS;
};
